import { ReactNode, memo } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import clsx from "clsx";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import InformationFillIcon from "remixicon-react/InformationFillIcon";
import { Link } from "react-router-dom";
import { Box } from "@mui/material";
import { theme } from "$utils/theme/bataillog";
import { Text } from "$components";
import { CompanyType } from "$types";
import { Loader } from "$components/Layout/Loader";

type SheetCTAProps = {
  color: string;
  sheetType: string;
  linkUrl?: string;
  icon: ReactNode;
  onClick?: (url: string) => void;
  title: string;
  stats: any;
  cantEditCurrentTab: boolean;
  selectedTab: CompanyType[];
  company: any;
};

function SheetCTA({
  color,
  title,
  icon,
  linkUrl,
  sheetType,
  onClick,
  stats,
  cantEditCurrentTab,
  selectedTab,
  company,
}: SheetCTAProps) {
  const classes = useStyles();
  if (!company) {
    return null;
  }

  const capitalize = sheetType.charAt(0).toUpperCase() + sheetType.slice(1);

  const inProgress = Array.isArray(stats[`inProgress${capitalize}`])
    ? stats[`inProgress${capitalize}`]?.length
    : stats[`inProgress${capitalize}`];
  const total = Array.isArray(stats[`total${capitalize}`])
    ? stats[`total${capitalize}`]?.length
    : stats[`total${capitalize}`];
  const infoBulle = stats[`${sheetType}Tooltip`];

  const handleClick = () => {
    if (company && onClick) {
      onClick(linkUrl);
    }
  };

  return (
    <Box
      className={clsx(classes.sheet)}
      style={{ background: theme.color[color] }}
      onClick={onClick ? handleClick : undefined}
    >
      <Box display="flex" alignItems="center">
        <Box className={classes.sheetIcon}>{icon}</Box>
        <Box className={classes.sheetDescription}>
          {stats[`isLoading${capitalize}`] ? (
            <Loader />
          ) : (
            <>
              <Text className={classes.inProgress}>{inProgress ?? 0}</Text>
              <Text className={classes.total}>/{total ?? 0}</Text>
            </>
          )}
        </Box>
        <Box className={classes.sheetTitle}>
          <Text>{title}</Text>
        </Box>
      </Box>

      <Box display="flex">
        {!cantEditCurrentTab && selectedTab.includes(CompanyType.CLIENT) && (
          <Box className={classes.sheetLink}>
            <Link className={classes.link} to={linkUrl || "/"}>
              Consulter
            </Link>
          </Box>
        )}
        <Box className={classes.sheetIconBackground}>{icon}</Box>
        <Box className={classes.sheetBubble}>
          <Tooltip title={infoBulle}>
            <Button>
              <InformationFillIcon />
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

const useStyles = createUseStyles(() => ({
  sheet: {
    display: "flex",
    flex: 1,
    fontFamily: theme.font.workSans,
    color: theme.color.white,
    padding: 10,
    borderRadius: 5,
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    justifyContent: "space-between",
    minHeight: 40,
  },
  sheetIcon: {
    display: "flex",
    alignItems: "center",
    padding: 5,
    background: "rgba(199, 199, 199, 0.2)",
    borderRadius: 5,
    "& img": {
      width: 22,
      height: 22,
    },
  },
  sheetDescription: {
    marginLeft: 10,
    "& span": {
      color: "inherit",
    },
  },
  sheetLink: {
    zIndex: 1,
    marginLeft: "auto",
  },
  sheetTitle: {
    marginLeft: theme.spacing(2),
    "& span": {
      color: "inherit",
    },
  },
  sheetBubble: {
    zIndex: 1,
    "& button": {
      padding: "0 !important",
      color: "inherit",
    },
  },
  total: {
    verticalAlign: "top",
  },
  inProgress: {
    fontSize: 29,
    verticalAlign: "top",
    fontWeight: "bold",
  },
  link: {
    color: "inherit",
    textDecoration: "underline",
    position: "relative",
    right: "-14px",
  },
  sheetIconBackground: {
    zIndex: 0,
    position: "absolute",
    right: -19,
    top: -22,
    width: 134,
    "& svg": {
      width: "100%",
      height: "auto",
      opacity: 0.3,
    },
    "& img": {
      width: 132,
      height: "auto",
      opacity: 0.3,
    },
  },
}));

export default memo(SheetCTA);
